import { useNavigate } from 'react-router-dom';
import Apertavel from '@/components/movimento/Apertavel';
import { corLiturgica, type OracaoHoje } from '@/lib/oracao';
import { marcarCuriosidadeVista, oracaoVistaHoje, rotuloHoje } from '@/lib/oracaoVista';

interface Props {
  oracao: OracaoHoje | null;
  /** A pessoa já rezou hoje (vem da mesma chamada da tela /oracao). */
  rezeiHoje?: boolean;
  carregando?: boolean;
}

// Bolinha de novidade: pulso lento (2 s). Com movimento reduzido ela fica
// parada — o aviso continua visível, só não pisca.
function Ponto() {
  return (
    <span
      className="ponto-novo ml-1 inline-block h-2.5 w-2.5 shrink-0 rounded-full bg-[#E11D48]"
      aria-hidden
    />
  );
}

// ---------------------------------------------------------------------------
// Cartão "Oração de hoje": a porta de entrada da capela. Verde profundo,
// cruz dourada e o tema na serifa — a mesma identidade da tela /oracao.
// ---------------------------------------------------------------------------
export default function CartaoOracao({ oracao, rezeiHoje = false, carregando = false }: Props) {
  const navigate = useNavigate();
  const nova = !oracaoVistaHoje();
  const tem = oracao?.tem === true;
  const cor = corLiturgica(oracao?.cor_liturgica);
  const tema = tem ? oracao?.tema || 'Oração de hoje' : 'A oração de hoje está sendo preparada';
  const referencia = tem ? oracao?.evangelho_ref : null;

  return (
    <Apertavel
      onClick={() => navigate('/oracao')}
      aria-label="Abrir a oração de hoje"
      className="mt-3 block w-full cursor-pointer text-left"
    >
      <div
        className="relative overflow-hidden rounded-[16px] px-4 py-4 shadow-pcard"
        style={{
          background: `radial-gradient(120% 90% at 100% 0%, ${cor.brilho} 0%, transparent 62%), ${cor.nave}`,
        }}
      >
        {/* barra lateral na cor litúrgica do dia */}
        <span
          className="absolute left-0 top-0 h-full w-[5px]"
          style={{ background: cor.fita }}
          aria-hidden
        />

        <div className="flex items-start gap-3 pl-1.5">
          <span
            className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-full"
            style={{ background: 'rgba(232,200,119,.14)', color: '#E8C877' }}
          >
            <i className="ri-cross-line text-[20px]" aria-hidden />
          </span>

          <div className="min-w-0 flex-1">
            {carregando ? (
              <div className="space-y-2 py-0.5" aria-hidden>
                <div className="h-3 w-40 animate-pulse rounded-full bg-white/10" />
                <div className="h-5 w-52 animate-pulse rounded-full bg-white/15" />
                <div className="h-3 w-28 animate-pulse rounded-full bg-white/10" />
              </div>
            ) : (
              <>
                <p className="text-[11.5px] font-semibold uppercase tracking-[0.08em] text-[#CDBFA6]">
                  Oração de hoje
                </p>

                <p className="mt-0.5 flex flex-wrap items-center gap-x-2 text-[11.5px] font-semibold text-[#CDBFA6]">
                  <span>{rotuloHoje()}</span>
                  {nova && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-[#E11D48]/15 px-1.5 py-0.5 text-[10.5px] font-bold uppercase tracking-wide text-[#FF8DA1]">
                      Nova <Ponto />
                    </span>
                  )}
                </p>

            <p
              className="mt-1 line-clamp-2 text-[22px] leading-[1.15] text-[#F3EAD8]"
              style={{ fontFamily: "'EB Garamond', Georgia, serif", fontWeight: 500 }}
            >
              {tema}
            </p>

            <p className="mt-1.5 flex items-center gap-1.5 text-[12px] font-medium text-[#CDBFA6]">
              {referencia && <span className="truncate">{referencia}</span>}
              {referencia && <span aria-hidden>·</span>}
              <span className="whitespace-nowrap">10 min</span>
              {rezeiHoje && (
                <>
                  <span aria-hidden>·</span>
                  <span className="whitespace-nowrap font-bold text-[#8FD3A8]">✓ feita hoje</span>
                </>
              )}
            </p>
              </>
            )}
          </div>

          <span className="mt-1 flex h-6 w-6 shrink-0 items-center justify-center text-[#CDBFA6]">
            <i className="ri-arrow-right-s-line text-xl" aria-hidden />
          </span>
        </div>
      </div>
    </Apertavel>
  );
}

interface SabiaProps {
  triboId: number;
  cor: string;
  texto: string;
  nova: boolean;
  semana: string;
}

// "Você sabia?" — a curiosidade da tribo, agora discreta logo abaixo da oração.
export function CartaoSabia({ triboId, cor, texto, nova, semana }: SabiaProps) {
  const navigate = useNavigate();
  return (
    <Apertavel
      onClick={() => {
        marcarCuriosidadeVista(semana);
        navigate(`/tribo/${triboId}`);
      }}
      className="mt-2 block w-full cursor-pointer text-left"
    >
      <div className="flex items-center gap-3 rounded-[16px] border border-background-200 bg-background-50 px-4 py-3">
        <span
          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full"
          style={{ background: `${cor}1F`, color: cor }}
        >
          <i className="ri-lightbulb-line text-[17px]" aria-hidden />
        </span>
        <div className="min-w-0 flex-1">
          <p className="flex items-center gap-1 text-[11px] font-bold uppercase tracking-wide text-foreground-500">
            Você sabia?
            {nova && <Ponto />}
          </p>
          <p className="mt-0.5 line-clamp-1 text-[13px] font-semibold text-foreground-800">
            {texto}
          </p>
        </div>
        <i className="ri-arrow-right-s-line text-foreground-400" aria-hidden />
      </div>
    </Apertavel>
  );
}